import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import PdfEditPage from '../components/PdfEditPage';
import s, { PdfEditResponseStruct } from '../utils/struct';
import useApiRequest from '../utils/useApiRequest';
import { apiUrl } from '../utils/apiRequest';
import { rotationPageRanges } from '../utils/page-range';
import getErrorName from '../utils/getErrorName';
import { sendEvent } from '../utils/analytics';

const PageRotatePdf = () => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'rotate-pdf']);
  const [fileTable, setFileTable] = useState(null);

  const {
    sendApiRequest,
    processing,
    error,
  } = useApiRequest({ url: '/api/pdf/rotate' });

  const handleSubmit = useCallback(({ pdfFiles }) => {
    if (processing) {
      return;
    }

    sendApiRequest({
      data: {
        files: pdfFiles.map((pdfFile) => ({
          name: pdfFile.file.file.name,
          filename: pdfFile.file.hash,
          pdfFile: pdfFile.file.password,
          pages: rotationPageRanges(pdfFile.rotations),
        })),
      },
      validateResponse(response) {
        s.assert(response.data, PdfEditResponseStruct);
      },
      onSuccess(response, time) {
        sendEvent({
          category: 'pdf-rotate',
          action: 'success',
          label: `${time}:${pdfFiles.length}`,
        });

        const firstFile = pdfFiles[0].file;
        const firstResultFiles = response.data.result[firstFile.hash].files;
        let fileType;
        let fileName;
        let fileSize;
        let fileUrl;

        if (pdfFiles.length > 1) {
          const { url, size } = response.data.result.zip;

          fileType = 'zip';
          fileUrl = url;
          fileName = 'files-rotate-pdf.zip';
          fileSize = size;
        } else {
          const { url, size } = firstResultFiles[0];

          fileType = 'pdf';
          fileUrl = url;
          fileName = firstFile.file.name;
          fileSize = size;
        }

        setFileTable({
          fileType,
          fileName,
          fileSize,
          fileUrl: apiUrl(fileUrl),
          compressable: false, // TODO: make compressable
        });
      },
      onError(error, time) {
        sendEvent({
          category: 'pdf-rotate',
          action: 'error',
          label: `${getErrorName(error)}:${time}:${pdfFiles.length}`,
        });
      },
    });
  }, [processing, sendApiRequest]);

  const meta = useMemo(() => ({
    title: t('rotate-pdf:meta.title'),
    description: t('rotate-pdf:meta.description'),
  }), [t]);

  const intro = useMemo(() => ({
    title: t('rotate-pdf:intro.title'),
    text: t('rotate-pdf:intro.text'),
  }), [t]);

  const dropzone = useMemo(() => ({
    options: {
      maxFiles: 10,
    },
  }), []);

  const howTo = useMemo(() => ({
    title: t('rotate-pdf:howto.title'),
    steps: t('rotate-pdf:howto.steps', { returnObjects: true }),
  }), [t]);

  const features = useMemo(() => ({
    content: t('rotate-pdf:features.content', { returnObjects: true }),
  }), [t]);

  const faq = useMemo(() => ({
    items: t('rotate-pdf:faq.items', { returnObjects: true }),
  }), [t]);

  const pdfViewer = useMemo(() => ({
    title: t('common:nav.rotate-pdf'),
    viewType: 'pages',
    rotatable: true,
    // без единой ротации сабмит заблокирован: бэк на пустой pages отвечает 422
    requireChange: 'rotations',
    processing,
  }), [t, processing]);

  const actionsGrid = useMemo(() => ({
    rotate: true,
    unlock: true,
    split: true,
    compress: true,
    pdf2word: true,
    pdf2excel: true,
    pdf2jpg: true,
    pdf2png: true,
    pdf2ppt: true,
  }), []);

  return (
    <PdfEditPage
      meta={meta}
      breadcrumb={t('common:nav.rotate-pdf')}
      intro={intro}
      dropzone={dropzone}
      features={features}
      faq={faq}
      pdfViewer={pdfViewer}
      fileTable={fileTable}
      actionsGrid={actionsGrid}
      onSubmit={handleSubmit}
      submitError={error}
      howTo={howTo}
    />
  );
};

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...await serverSideTranslations(locale, [
      'common',
      'convert-and-edit',
      'rotate-pdf',
    ]),
  },
});

export default PageRotatePdf;
